import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AutocompleteComponent } from './components/autocomplete/autocomplete.component';
import { EditableComponent, EditModeDirective, ViewModeDirective } from '@ngneat/edit-in-place';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import json from 'highlight.js/lib/languages/json';
import typescript from 'highlight.js/lib/languages/typescript';
import javascript from 'highlight.js/lib/languages/javascript';
import { HIGHLIGHT_OPTIONS, HighlightModule } from 'ngx-highlightjs';
import { PieChartModule } from '@swimlane/ngx-charts';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { AngularMaterialModule } from '@shared/angular-material.module';
import { ClipboardModule } from '@angular/cdk/clipboard';

// modules
import { SearchbarModule } from '@shared/components/searchbar-v1/searchbar.module';
import { DatatableModule } from '@shared/components/datatable/datatable.module';
import { SearchbarV2Module } from '@shared/components/searchbar-v2/searchbar-v2.module';
import { PickerModule } from '@shared/components/picker/picker.module';
import { MultiPickerModule } from '@shared/components/multi-picker/multi-picker.module';
import { MessageBoxModule } from '@shared/components/message-box/message-box.module';
import { DrawerModule } from '@shared/components/drawer/drawer.module';
import { FilterBarModule } from '@shared/components/filter-bar/filter-bar.module';

// component
import { DetailCardComponent } from '@shared/components/detail-card/detail-card.component';
import { DetailCardFieldComponent } from '@shared/components/detail-card/detail-card-field/detail-card-field.component';
import { DonutChartComponent } from '@shared/components/donut-chart/donut-chart.component';
import { ErrorCatComponent } from '@shared/components/error-cat/error-cat.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { InventoriesTableComponent } from '@shared/components/inventories-table/inventories-table.component';
import { MagicboxDeleteMetadataDialogComponent } from '@shared/components/magicbox-table/magicbox-delete-metadata-dialog.component';
import { MagicboxTableComponent } from '@shared/components/magicbox-table/magicbox-table.component';
import { MagicboxTableV2Component } from '@shared/components/magicbox-table-v2/magicbox-table-v2.component';
import { NotesComponent } from '@shared/components/notes/notes.component';
import { UserAuthorizationsComponent } from '@shared/components/user-authorizations/user-authorizations.component';
import { UserAuthorizationsFormComponent } from '@shared/components/user-authorizations-form/user-authorizations-form.component';
import { AclsDetailsDialogComponent } from '@shared/components/acls-details-dialog/acls-details-dialog.component';
import { BotAclsDetailsDialogComponent } from './components/bot-acls-details-dialog/bot-acls-details-dialog.component';
import { AvatarComponent } from '@shared/components/avatar/avatar.component';
import { BooleanIcoComponent } from '@shared/components/ux/boolean-ico.component';
import { CompaniesPickerComponent } from '@shared/components/companies-picker/companies-picker.component';
import { CompaniesTableComponent } from '@shared/components/companies-table/companies-table.component';
import { CompanyAvatarComponent } from '@shared/components/avatar/company-avatar.component';
import { CompanyPickerComponent } from '@shared/components/company-picker/company-picker.component';
import { DynamicListLoadMoreComponent } from '@shared/components/dynamic-list/dynamic-list-load-more.component';
import { DynamicTableComponent } from '@shared/components/dynamic-table/dynamic-table.component';
import { FieldComponent } from '@shared/components/field/field.component';
import { InArrayComponent } from '@shared/components/in-array/in-array.component';
import { InventoryAvatarComponent } from '@shared/components/avatar/inventory-avatar.component';
import { LanguageComponent } from '@shared/components/language/language.component';
import { LanguageFlagComponent } from '@shared/components/language-flag/language-flag.component';
import { LanguageInputComponent } from '@shared/components/language-flag/language-input.component';
import { MenuCheckboxComponent } from '@shared/components/menu-checkbox/menu-checkbox.component';
import { MonthsMatrixComponent } from '@shared/components/ux/months-matrix.component';
import { PageLoaderComponent } from '@shared/components/loader/page-loader.component';
import { PhoneInputComponent } from '@shared/components/form/phone-input.component';
import { ProjectAuthorizationsComponent } from '@shared/components/project-authorizations/project-authorizations.component';
import { ProjectAvatarComponent } from '@shared/components/avatar/project-avatar.component';
import { BusinessProjectAvatarComponent } from '@shared/components/avatar/businessproject-avatar.component';
import { ProjectGroupInputComponent } from '@shared/components/group/project-group-input.component';
import { ProjectsTableComponent } from '@shared/components/projects-table/projects-table.component';
import { ProjectTagInputComponent } from '@shared/components/tag/project-tag-input.component';
import { RolesDetailsDialogComponent } from '@shared/components/roles-details-dialog/roles-details-dialog.component';
import { SelectInputComponent } from '@shared/components/select/select-input.component';
import { SourceComponent } from '@shared/components/source/source.component';
import { SupportLevelComponent } from '@shared/components/ux/support-level.component';
import { TextMoreComponent } from '@shared/components/ux/text-more.component';
import { TicketAvatarComponent } from '@shared/components/avatar/ticket-avatar.component';
import { TicketsTableComponent } from '@shared/components/tickets-table/tickets-table.component';
import { UnsafeLinkComponent } from '@shared/components/unsafe-link/unsafe-link';
import { UserAvatarComponent } from '@shared/components/avatar/user-avatar.component';
import { UserPickerComponent } from '@shared/components/user-picker/user-picker.component';
import { UsersPickerComponent } from '@shared/components/users-picker/users-picker.component';
import { UsersTableComponent } from '@shared/components/users-table/users-table.component';
import { UserTagInputComponent } from '@shared/components/tag/user-tag-input.component';
import { InventoryTagInputComponent } from '@shared/components/tag/inventory-tag-input.component';
import { WysiwygComponent } from '@shared/components/wysiwyg/wysiwyg.component';
import { CollaboratorPickerComponent } from '@shared/components/watchlist-picker/collaborator-picker.component';
import { WatchlistPickerComponent } from '@shared/components/watchlist-picker/watchlist-picker.component';
import { FormLinksComponent } from '@shared/components/form-links/form-links.component';
import {
    CreateGroupDialogComponent
} from '@shared/components/dialog-components/groups/create-group-dialog.component';
import { BotAuthorizationsFormComponent } from '@shared/components/bot-authorizations-form/bot-authorizations-form.component';
import { ProjectsRolesTableComponent } from '@shared/components/bot-authorizations-form/projects-roles-table/projects-roles-table.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { SynappsTreeViewComponent } from '@shared/components/tree-view/tree-view.component';
import { LoadingCheckComponent } from '@shared/components/loading-check/loading-check.component';
import { MessageBoxAclComponent } from '@shared/components/message-box-acl/message-box-acl.component';
import { SubscriptionComponent } from './components/subscription/subscription.component';
import { SubscriptionsComponent } from './components/subscriptions/subscriptions.component';

// directive
import { MdlUpgradeElementsDirective } from '@shared/directives/mdl-upgrade-elements.directive';
import { FieldDirective } from '@shared/directives/field.directive';
import { FieldFocusDirective } from '@shared/directives/field-focus.directive';
import { FieldLabelDirective } from '@shared/directives/field-label.directive';
import { FieldValueDirective } from '@shared/directives/field-value.directive';
import { GridCellDirective } from '@shared/directives/grid-cell.directive';
import { GridDirective } from '@shared/directives/grid.directive';
import { LayoutContainerDirective } from '@shared/directives/layout-container.directive';
import { LayoutContentDirective } from '@shared/directives/layout-content.directive';
import { LayoutSidebarDirective } from '@shared/directives/layout-sidebar.directive';
import { ButtonLoadingDirective } from '@shared/directives/button-loading.directive';
// service
import { WindowRefService } from '@shared/services/window-ref/window-ref.service';
import { SnackbarService } from '@app/services/snackbar.service';
import { LanguageService } from '@shared/components/language/language.service';
import { SourceService } from '@shared/components/source/source.service';

// pipes
import { InArrayPipe } from '@shared/pipes/in-array.pipe';
import { HighlightPipe } from '@shared/pipes/highlight';
import { SynMarkdownComponent } from './components/markdown/syn-markdown';
import { ValidationIconTooltipComponent } from './components/validation-icon-tooltip/validation-icon-tooltip.component';
import { MatTreeModule } from '@angular/material/tree';
import { BotAclsWarningComponent } from './components/bot-acls-warning/bot-acls-warning.component';
import { AppTagInputComponent } from './components/tag/app-tag-input.component';
import { AssignmentGroupConfigComponent } from './components/assignment-group-config/assignment-group-config.component';
import { HeaderActionsButtonComponent } from './components/header/header-actions-button.component';
import { LayoutDetailComponent } from './components/layout-detail/layout-detail.component';
import { HeaderActionsService } from './components/layout-detail/header-actions-service';
import { DateAdapter } from '@angular/material/core';
import { FranceDateAdapter } from './services/france-date-adapter';
import { AuditDiffComponent } from './components/audit-diff/audit-diff.component';
import { AuditHistoryComponent } from './components/audit-history/audit-history.component';
import { AuditEntityComponent } from './components/audit-entity/audit-entity.component';
import { SelectOptionRoleComponent } from './components/select-option-role/select-option-role.component';
import { FullpageLoaderComponent } from './components/fullpage-loader/fullpage-loader.component';
import { AlertComponent } from './components/alert/alert.component';
import { ClipboardService } from '@app/services/clipboard.service';
import { CopyToClipboardDirective } from './directives/copy-to-clipboard.directive';
import { NoSelectedFilterComponent } from './components/no-selected-filter/no-selected-filter';
import { InventoryBulkEditComponent } from './components/inventory-bulk-edit/inventory-bulk-edit.component';
import { ErrorMessageComponent } from './components/error-message/error-message.component';
import { SlaServiceLevelComponent } from './components/sla-service-level/sla-service-level.component';
import { ProjectBulkEditComponent } from './components/project-bulk-edit/project-bulk-edit.component';
import { BulkEditToggleComponent } from './components/bulk-edit-toggle/bulk-edit-toggle.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';

export function hljsLanguages() {
    return [
        { name: 'typescript', func: typescript },
        { name: 'javascript', func: javascript },
        { name: 'json', func: json },
    ];
}

const COMPONENTS = [
    NoSelectedFilterComponent,
    LoadingCheckComponent,
    SynMarkdownComponent,
    AutocompleteComponent,
    AvatarComponent,
    BooleanIcoComponent,
    CompanyAvatarComponent,
    DynamicTableComponent,
    ErrorCatComponent,
    CompaniesPickerComponent,
    CompaniesTableComponent,
    CompanyPickerComponent,
    DynamicListLoadMoreComponent,
    InArrayComponent,
    InArrayPipe,
    HighlightPipe,
    InventoryAvatarComponent,
    LanguageComponent,
    LanguageFlagComponent,
    LanguageInputComponent,
    MenuCheckboxComponent,
    MonthsMatrixComponent,
    NotesComponent,
    PageLoaderComponent,
    PhoneInputComponent,
    ProjectAvatarComponent,
    BusinessProjectAvatarComponent,
    TicketAvatarComponent,
    ProjectGroupInputComponent,
    ProjectTagInputComponent,
    ProjectsTableComponent,
    ProjectAuthorizationsComponent,
    TicketsTableComponent,
    SelectInputComponent,
    SourceComponent,
    SupportLevelComponent,
    TextMoreComponent,
    AclsDetailsDialogComponent,
    BotAclsDetailsDialogComponent,
    BotAclsWarningComponent,
    RolesDetailsDialogComponent,
    UserAvatarComponent,
    UserPickerComponent,
    UsersPickerComponent,
    UsersTableComponent,
    UserTagInputComponent,
    UserAuthorizationsComponent,
    UserAuthorizationsFormComponent,
    FieldComponent,
    DetailCardFieldComponent,
    DetailCardComponent,
    FormLinksComponent,
    MagicboxTableComponent,
    MagicboxTableV2Component,
    MagicboxDeleteMetadataDialogComponent,
    InventoriesTableComponent,
    DonutChartComponent,
    HeaderComponent,
    WysiwygComponent,
    CollaboratorPickerComponent,
    WatchlistPickerComponent,
    CreateGroupDialogComponent,
    UnsafeLinkComponent,
    InventoryTagInputComponent,
    BotAuthorizationsFormComponent,
    ProjectsRolesTableComponent,
    ConfirmDialogComponent,
    SynappsTreeViewComponent,
    AppTagInputComponent,
    ValidationIconTooltipComponent,
    SynappsTreeViewComponent,
    MessageBoxAclComponent,
    AssignmentGroupConfigComponent,
    HeaderActionsButtonComponent,
    LayoutDetailComponent,
    AuditHistoryComponent,
    AuditDiffComponent,
    AuditEntityComponent,
    SelectOptionRoleComponent,
    FullpageLoaderComponent,
    AlertComponent,
    SubscriptionComponent,
    SubscriptionsComponent,
    InventoryBulkEditComponent,
    ErrorMessageComponent,
    SlaServiceLevelComponent,
    ProjectBulkEditComponent,
    BulkEditToggleComponent
];

const DIRECTIVES_AND_PIPES = [
    InArrayPipe,
    FieldDirective,
    FieldFocusDirective,
    FieldLabelDirective,
    FieldValueDirective,
    GridCellDirective,
    GridDirective,
    LayoutContainerDirective,
    LayoutContentDirective,
    LayoutSidebarDirective,
    MdlUpgradeElementsDirective,
    ButtonLoadingDirective,
    CopyToClipboardDirective,
];

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        AngularMaterialModule,
        ClipboardModule,
        RouterModule,
        FlexLayoutModule,
        PieChartModule,
        PickerModule,
        MultiPickerModule,
        SearchbarModule,
        SearchbarV2Module,
        DatatableModule,
        MessageBoxModule,
        DrawerModule,
        HighlightModule,
        InfiniteScrollModule,
        MatTreeModule,
        EditableComponent,
        EditModeDirective,
        ViewModeDirective,
        FilterBarModule,
        ViewModeDirective,
        MatProgressBarModule
    ],
    declarations: [
        ...COMPONENTS,
        ...DIRECTIVES_AND_PIPES,
    ],
    exports: [
        AngularMaterialModule,
        ClipboardModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        FlexLayoutModule,
        PickerModule,
        MultiPickerModule,
        SearchbarModule,
        SearchbarV2Module,
        DatatableModule,
        MessageBoxModule,
        HighlightModule,
        DrawerModule,
        EditableComponent,
        EditModeDirective,
        ViewModeDirective,
        InfiniteScrollModule,
        FilterBarModule,
        ...DIRECTIVES_AND_PIPES,
        ...COMPONENTS,
    ],
    providers: [
        LanguageService,
        SnackbarService,
        ClipboardService,
        WindowRefService,
        SourceService,
        HeaderActionsService,
        { provide: DateAdapter, useClass: FranceDateAdapter },
        {
            provide: HIGHLIGHT_OPTIONS,
            useValue: {
                coreLibraryLoader: () => import('highlight.js/lib/core'),
                lineNumbersLoader: () => import('ngx-highlightjs/line-numbers'),
                languages: {
                    typescript: () => import('highlight.js/lib/languages/typescript'),
                    json: () => import('highlight.js/lib/languages/json'),
                    javascript: () => import('highlight.js/lib/languages/javascript')
                },
            }
        }
    ]
})
export class SharedModule {
}
